import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useData } from '../../contexts/DataContext';
import api from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Separator } from '../../components/ui/separator';
import { Checkbox } from '../../components/ui/checkbox';
import { toast } from 'sonner';
import { Cog, Plus, Edit, Trash2, QrCode, List, Grid3X3, Printer, Download, FileText, History, Power, CheckCircle2, XCircle, RefreshCw, ChevronRight, Star, Upload, FileSpreadsheet } from 'lucide-react';
import { DataTable } from '../../components/ui/data-table';
import { AuditLogDialog } from '../../components/AuditLogDialog';
import { Combobox } from '../../components/ui/combobox';
import { useIsMobile } from '../../hooks/useIsMobile';

const EquipmentPage = () => {
  const navigate = useNavigate();
  const { plants, selectedPlant, locations, selectedLocation } = useData();
  const isMobile = useIsMobile(1024);
  const [equipment, setEquipment] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [qrDialog, setQrDialog] = useState(null);
  const [activeView, setActiveView] = useState('table');
  const [selectedIds, setSelectedIds] = useState([]);
  const [auditDialog, setAuditDialog] = useState(null);
  const [statusFilter, setStatusFilter] = useState('active');
  const [showBulkUpdateDialog, setShowBulkUpdateDialog] = useState(false);
  const [bulkUpdateFields, setBulkUpdateFields] = useState({});
  const [bulkUpdateValues, setBulkUpdateValues] = useState({});
  const [importing, setImporting] = useState(false);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const importFileRef = React.useRef(null);
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    plant_id: '',
    location_id: '',
    parent_id: null,
    description: '',
    manufacturer: '',
    model: '',
    serial_number: '',
    inventory_number: '',
    utilizare: '',
    an_fabricatie: '',
    putere_absorbita: '',
    category_id: '',
    ticket_priority: ''
  });

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (selectedPlant && selectedPlant !== 'all') params.plant_id = selectedPlant;
      if (selectedLocation && selectedLocation !== 'all') params.location_id = selectedLocation;
      if (statusFilter !== 'active') params.show_inactive = true;
      
      const [equipmentRes, categoriesRes] = await Promise.all([
        api.get('/equipment', { params }),
        api.get('/equipment-categories')
      ]);
      let data = equipmentRes.data;
      if (statusFilter === 'inactive') data = data.filter(e => e.is_active === false);
      else if (statusFilter === 'active') data = data.filter(e => e.is_active !== false);
      setEquipment(data);
      setCategories(categoriesRes.data);
    } catch (error) {
      toast.error('Eroare la incarcarea datelor');
    } finally {
      setLoading(false);
    }
  }, [selectedPlant, selectedLocation, statusFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);


  const handleSubmit = async (e) => {
    e.preventDefault();
    // Drop empty strings so Optional[int] / Optional[str] fields don't fail Pydantic validation
    const payload = Object.fromEntries(
      Object.entries(formData).filter(([, v]) => v !== '' && v !== undefined)
    );
    try {
      if (editItem) {
        await api.put(`/equipment/${editItem.id}`, payload);
        toast.success('Echipament actualizat!');
      } else {
        await api.post('/equipment', payload);
        toast.success('Echipament creat!');
      }
      setShowAddDialog(false);
      setEditItem(null);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Eroare la salvare');
    }
  };

  const handleEdit = (item) => {
    setEditItem(item);
    const resolvedPlantId = item.plant_id || (item.plant_name ? plants.find(p => p.name === item.plant_name)?.id : '') || '';
    const resolvedLocationId = item.location_id || (item.location_name ? locations.find(l => l.name === item.location_name)?.id : '') || '';
    setFormData({
      name: item.name,
      code: item.code,
      plant_id: resolvedPlantId,
      location_id: resolvedLocationId,
      parent_id: item.parent_id,
      description: item.description || '',
      manufacturer: item.manufacturer || '',
      model: item.model || '',
      serial_number: item.serial_number || '',
      inventory_number: item.inventory_number || '',
      utilizare: item.utilizare || '',
      an_fabricatie: item.an_fabricatie || '',
      putere_absorbita: item.putere_absorbita || '',
      category_id: item.category_id || '',
      ticket_priority: item.ticket_priority || ''
    });
    setShowAddDialog(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Sigur doriti sa stergeti acest echipament?')) return;
    try {
      await api.delete(`/equipment/${id}`);
      toast.success('Echipament sters!');
      fetchData();
    } catch (error) {
      toast.error('Eroare la stergere');
    }
  };

  const handleToggleActive = async (id, currentStatus) => {
    try {
      await api.patch(`/equipment/${id}/toggle-active`);
      toast.success(currentStatus ? 'Echipament dezactivat' : 'Echipament activat');
      fetchData();
    } catch (error) {
      toast.error('Eroare la schimbarea statusului');
    }
  };

  const handleBulkStatus = async (isActive) => {
    if (!selectedIds.length) return;
    const action = isActive ? 'activa' : 'dezactiva';
    if (!window.confirm(`Sigur doriti sa ${action}ti ${selectedIds.length} echipamente?`)) return;
    try {
      await api.patch('/equipment/bulk-status', { ids: selectedIds, is_active: isActive });
      toast.success(`${selectedIds.length} echipamente ${isActive ? 'activate' : 'dezactivate'}`);
      setSelectedIds([]);
      fetchData();
    } catch (error) {
      toast.error('Eroare la operatia bulk');
    }
  };

  const handleBulkUpdateFields = async () => {
    const activeFields = Object.keys(bulkUpdateFields).filter(k => bulkUpdateFields[k]);
    if (!activeFields.length) { toast.error('Selectati cel putin un camp'); return; }
    const fields = {};
    activeFields.forEach(k => { fields[k] = bulkUpdateValues[k] ?? ''; });
    try {
      const res = await api.patch('/equipment/bulk-update-fields', { ids: selectedIds, fields });
      toast.success(`${res.data.modified} echipamente actualizate (${res.data.fields_updated.length} campuri)`);
      setShowBulkUpdateDialog(false);
      setBulkUpdateFields({});
      setBulkUpdateValues({});
      setSelectedIds([]);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Eroare la actualizarea in masa');
    }
  };

  const openBulkUpdateDialog = () => {
    setBulkUpdateFields({});
    setBulkUpdateValues({});
    setShowBulkUpdateDialog(true);
  };

  const handleBulkDelete = async () => {
    if (!selectedIds.length) return;
    if (!window.confirm(`Sigur doriti sa stergeti DEFINITIV ${selectedIds.length} echipamente? Aceasta actiune nu poate fi anulata!`)) return;
    try {
      await api.patch('/equipment/bulk-delete', { ids: selectedIds });
      toast.success(`${selectedIds.length} echipamente sterse`);
      setSelectedIds([]);
      fetchData();
    } catch (error) {
      toast.error('Eroare la stergerea in masa');
    }
  };

  const handleExportExcel = async () => {
    try {
      const params = selectedPlant && selectedPlant !== 'all' ? `?plant_id=${selectedPlant}` : '';
      const response = await api.get(`/export/equipment${params}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `echipamente_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Export Excel finalizat!');
    } catch (error) {
      toast.error('Eroare la export');
    }
  };

  const handleImportExcel = async () => {
    if (!importFile) { toast.error('Selecteaza un fisier Excel'); return; }
    try {
      setImporting(true);
      setImportResult(null);
      const formDataUpload = new FormData();
      formDataUpload.append('file', importFile);
      const response = await api.post('/import/equipment', formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setImportResult({ success: true, message: response.data.message, imported: response.data.imported, errors: response.data.errors || [] });
      toast.success(response.data.message);
      setImportFile(null);
      if (importFileRef.current) importFileRef.current.value = '';
      fetchData();
    } catch (error) {
      setImportResult({ success: false, message: error.response?.data?.detail || 'Eroare la import', errors: [] });
      toast.error('Eroare la import');
    } finally {
      setImporting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '', code: '', plant_id: selectedPlant !== 'all' ? selectedPlant : '',
      location_id: '', parent_id: null, description: '', manufacturer: '',
      model: '', serial_number: '', inventory_number: '', utilizare: '', an_fabricatie: '', putere_absorbita: '', category_id: '',
      ticket_priority: ''
    });
  };

  const [showQrExportDialog, setShowQrExportDialog] = useState(false);
  const [qrFields, setQrFields] = useState({
    code: true,
    name: true,
    location: true,
    serial: true,
    inventory: true,
    manufacturer: false,
    category: false,
    plant: false
  });

  const qrFieldOptions = [
    { key: 'code', label: 'Cod' },
    { key: 'name', label: 'Denumire' },
    { key: 'location', label: 'Locatie' },
    { key: 'serial', label: 'Serie' },
    { key: 'inventory', label: 'Numar Inventar' },
    { key: 'manufacturer', label: 'Producator / Model' },
    { key: 'category', label: 'Categorie' },
    { key: 'plant', label: 'Sediu' }
  ];

  const showQrCode = async (eq) => {
    try {
      const response = await api.get(`/equipment/${eq.id}/qr`);
      setQrDialog({ ...response.data, id: eq.id });
    } catch (error) {
      toast.error('Eroare la generarea codului QR');
    }
  };

  const downloadQrDirect = async (eq) => {
    try {
      const response = await api.get(`/equipment/${eq.id}/qr`);
      if (response.data?.qr_code) {
        const link = document.createElement('a');
        link.href = `data:image/png;base64,${response.data.qr_code}`;
        link.download = `QR_${eq.code || eq.name}.png`;
        link.click();
        toast.success(`QR descarcat: ${eq.code}`);
      }
    } catch (error) {
      toast.error('Eroare la descarcarea QR');
    }
  };

  const downloadEquipmentPdf = async (eq) => {
    try {
      const response = await api.post(
        `/equipment/qr-export?format=large&fields=code,name,serial,inventory,location`,
        [eq.id],
        { responseType: 'blob' }
      );
      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = blobUrl;
      link.setAttribute('download', `QR_${eq.code || 'echipament'}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(`PDF QR descarcat: ${eq.code}`);
    } catch (error) {
      toast.error('Eroare la generare PDF');
    }
  };

  const handleExportQrCodes = async (format = 'standard') => {
    try {
      const ids = selectedIds.length > 0 ? selectedIds : equipment.map(e => e.id);
      const fields = Object.keys(qrFields).filter(k => qrFields[k]).join(',');
      const response = await api.post(`/equipment/qr-export?format=${format}&fields=${fields}`, ids, { responseType: 'blob' });
      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = blobUrl;
      link.setAttribute('download', `qr_echipamente_${ids.length}_${format}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      setShowQrExportDialog(false);
      toast.success(`Export QR (${format}) pentru ${ids.length} echipamente generat!`);
    } catch (error) {
      toast.error('Eroare la export QR');
    }
  };

  const handleExportQrZip = async () => {
    try {
      const ids = selectedIds.length > 0 ? selectedIds : equipment.map(e => e.id);
      const fields = Object.keys(qrFields).filter(k => qrFields[k]).join(',');
      const response = await api.post(`/equipment/qr-export-zip?fields=${fields}`, ids, { responseType: 'blob' });
      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = blobUrl;
      link.setAttribute('download', `qr_echipamente_${ids.length}.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      setShowQrExportDialog(false);
      toast.success(`Arhiva ZIP cu ${ids.length} imagini QR generata!`);
    } catch (error) {
      toast.error('Eroare la export ZIP');
    }
  };

  // Prepare data for DataTable
  const equipmentData = equipment.map(eq => ({
    ...eq,
    category_display: eq.category_name || '-',
    location_display: eq.location_name || '-',
    plant_display: eq.plant_name || '-',
    parent_display: eq.parent_id ? equipment.find(e => e.id === eq.parent_id)?.name || '-' : '-'
  }));

  // Columns for DataTable
  const tableColumns = [
    {
      key: 'code',
      label: 'Cod',
      sortable: true,
      filterable: true,
      width: 'w-28',
      render: (value) => (
        <Badge variant="outline" className="font-mono text-xs">{value}</Badge>
      )
    },
    {
      key: 'name',
      label: 'Nume Echipament',
      sortable: true,
      filterable: true,
      render: (value, row) => (
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded bg-blue-100 flex items-center justify-center">
            <Cog className="w-4 h-4 text-[#0052CC]" />
          </div>
          <div>
            <p className="font-medium">{value}</p>
            {row.manufacturer && (
              <p className="text-xs text-slate-500">{row.manufacturer} {row.model}</p>
            )}
          </div>
        </div>
      )
    },
    {
      key: 'category_display',
      label: 'Categorie',
      sortable: true,
      filterable: true,
      render: (value) => value !== '-' ? (
        <Badge variant="secondary">{value}</Badge>
      ) : <span className="text-slate-400">-</span>
    },
    {
      key: 'location_display',
      label: 'Locatie',
      sortable: true,
      filterable: true
    },
    {
      key: 'plant_display',
      label: 'Sediu',
      sortable: true,
      filterable: true
    },
    {
      key: 'serial_number',
      label: 'Serie',
      sortable: true,
      filterable: true,
      render: (value) => value || <span className="text-slate-400">-</span>
    },
    {
      key: 'inventory_number',
      label: 'Nr. Inventar',
      sortable: true,
      filterable: true,
      render: (value) => value || <span className="text-slate-400">-</span>
    },
    {
      key: 'utilizare',
      label: 'Utilizare',
      sortable: true,
      filterable: true,
      render: (value) => value || <span className="text-slate-400">-</span>
    },
    {
      key: 'an_fabricatie',
      label: 'An',
      sortable: true,
      render: (value) => value || <span className="text-slate-400">-</span>
    },
    {
      key: 'putere_absorbita',
      label: 'Putere',
      sortable: true,
      render: (value) => value || <span className="text-slate-400">-</span>
    },
    {
      key: 'is_active',
      label: 'Status',
      sortable: true,
      render: (value) => value === false
        ? <Badge variant="outline" className="bg-red-50 text-red-600 border-red-200">Inactiv</Badge>
        : <Badge variant="outline" className="bg-green-50 text-green-600 border-green-200">Activ</Badge>
    },
    {
      key: 'actions',
      label: 'Actiuni',
      sortable: false,
      filterable: false,
      width: 'w-48',
      render: (_, row) => (
        <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleToggleActive(row.id, row.is_active !== false)}
            title={row.is_active === false ? 'Activeaza' : 'Dezactiveaza'}
            data-testid={`toggle-eq-${row?.code}`}
            className={row.is_active === false ? 'text-green-600' : 'text-orange-500'}
          >
            <Power className="w-4 h-4" />
          </Button>
          <Button variant="ghost" size="sm" onClick={() => navigate(`/equipment-passport/${row.id}`)} title="Pasaport Digital" data-testid={`passport-${row?.code}`} className="text-purple-600">
            <FileText className="w-4 h-4" />
          </Button>
          <Button variant="ghost" size="sm" onClick={() => showQrCode(row)} title="Cod QR" data-testid={`qr-view-${row?.code}`}>
            <QrCode className="w-4 h-4" />
          </Button>
          <Button variant="ghost" size="sm" onClick={() => downloadQrDirect(row)} title="Descarca QR rapid" data-testid={`qr-download-${row?.code}`} className="text-blue-600">
            <Download className="w-4 h-4" />
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setAuditDialog(row)} title="Istoric Modificari" data-testid={`audit-eq-${row?.code}`} className="text-amber-600">
            <History className="w-4 h-4" />
          </Button>
          <Button variant="ghost" size="sm" onClick={() => handleEdit(row)} data-testid={`edit-eq-${row?.code}`}>
            <Edit className="w-4 h-4" />
          </Button>
          <Button variant="ghost" size="sm" onClick={() => handleDelete(row.id)} className="text-red-600" data-testid={`delete-eq-${row?.code}`}>
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      )
    }
  ];

  return (
    <div className="space-y-6" data-testid="equipment-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl lg:text-4xl font-bold text-slate-900">Echipamente</h1>
          <p className="text-slate-600 mt-1">Gestioneaza echipamentele si componentele ({equipment.length} total)</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center bg-slate-100 rounded-lg p-1" data-testid="status-filter-equipment">
            {[{v:'active',l:'Active'},{v:'inactive',l:'Inactive'},{v:'all',l:'Toate'}].map(f => (
              <button key={f.v} onClick={() => setStatusFilter(f.v)}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${statusFilter === f.v ? 'bg-white shadow text-slate-900' : 'text-slate-500 hover:text-slate-700'}`}
                data-testid={`filter-eq-${f.v}`}
              >{f.l}</button>
            ))}
          </div>
          <Button variant="outline" onClick={() => setShowQrExportDialog(true)} className="h-10">
            <Printer className="w-4 h-4 mr-2" />
            Print QR {selectedIds.length > 0 && `(${selectedIds.length})`}
          </Button>
          <Button variant="outline" onClick={handleExportExcel} className="h-10" data-testid="export-equipment-excel">
            <Download className="w-4 h-4 mr-2" />
            Export
          </Button>
          <Button variant="outline" onClick={() => { setShowImportDialog(true); setImportResult(null); setImportFile(null); }} className="h-10" data-testid="import-equipment-excel">
            <Upload className="w-4 h-4 mr-2" />
            Import
          </Button>
          <Tabs value={activeView} onValueChange={setActiveView} className="mr-2">
            <TabsList className="h-10">
              <TabsTrigger value="table" className="px-3">
                <List className="w-4 h-4" />
              </TabsTrigger>
              <TabsTrigger value="cards" className="px-3">
                <Grid3X3 className="w-4 h-4" />
              </TabsTrigger>
            </TabsList>
          </Tabs>
          <Dialog open={showAddDialog} onOpenChange={(open) => {
            setShowAddDialog(open);
            if (!open) { setEditItem(null); resetForm(); }
          }}>
            <DialogTrigger asChild>
              <Button className="btn-primary h-12" data-testid="add-equipment-btn">
                <Plus className="w-5 h-5 mr-2" />
                ECHIPAMENT NOU
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editItem ? 'Editeaza Echipament' : 'Adauga Echipament'}</DialogTitle>
                <DialogDescription>Completati informatiile despre echipament</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Cod</Label>
                    <Input
                      value={editItem ? formData.code : '(auto-generat)'}
                      disabled
                      className="h-11 bg-slate-100 text-slate-500 font-mono"
                      data-testid="equipment-code-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Categorie</Label>
                    <Select
                      value={formData.category_id}
                      onValueChange={(value) => setFormData({ ...formData, category_id: value })}
                    >
                      <SelectTrigger className="h-11">
                        <SelectValue placeholder="Selecteaza" />
                      </SelectTrigger>
                      <SelectContent>
                        {categories.map((cat) => (
                          <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Nume *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    className="h-11"
                    placeholder="ex: Linie asamblare A"
                    data-testid="equipment-name-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Sediu *</Label>
                    <Select
                      value={formData.plant_id}
                      onValueChange={(value) => setFormData({ ...formData, plant_id: value, location_id: '' })}
                      required
                    >
                      <SelectTrigger className="h-11">
                        <SelectValue placeholder="Selecteaza" />
                      </SelectTrigger>
                      <SelectContent>
                        {plants.map((plant) => (
                          <SelectItem key={plant.id} value={plant.id}>{plant.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Locatie *</Label>
                    <Select
                      value={formData.location_id}
                      onValueChange={(value) => setFormData({ ...formData, location_id: value })}
                      required
                    >
                      <SelectTrigger className="h-11">
                        <SelectValue placeholder="Selecteaza" />
                      </SelectTrigger>
                      <SelectContent>
                        {locations.filter(l => !formData.plant_id || l.plant_id === formData.plant_id).map((loc) => (
                          <SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Prioritate la deschidere tichete</Label>
                  <Select
                    value={formData.ticket_priority}
                    onValueChange={(value) => setFormData({ ...formData, ticket_priority: value })}
                  >
                    <SelectTrigger className="h-11" data-testid="eq-ticket-priority">
                      <SelectValue placeholder="Nesetat (implicit Mediu)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="critical">Critic</SelectItem>
                      <SelectItem value="urgent">Urgent</SelectItem>
                      <SelectItem value="high">Ridicat</SelectItem>
                      <SelectItem value="medium">Mediu</SelectItem>
                      <SelectItem value="low">Scazut</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Echipament parinte (optional)</Label>
                  <Combobox
                    items={equipment.filter(e => e.id !== editItem?.id)}
                    value={formData.parent_id || ''}
                    onSelect={(id) => setFormData({ ...formData, parent_id: id || null })}
                    placeholder="Niciun parinte"
                    searchPlaceholder="Cauta echipament parinte..."
                    emptyText="Niciun echipament gasit."
                    codeKey="code"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Producator</Label>
                    <Input
                      value={formData.manufacturer}
                      onChange={(e) => setFormData({ ...formData, manufacturer: e.target.value })}
                      className="h-11"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Model</Label>
                    <Input
                      value={formData.model}
                      onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                      className="h-11"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Serie</Label>
                  <Input
                    value={formData.serial_number}
                    onChange={(e) => setFormData({ ...formData, serial_number: e.target.value })}
                    className="h-11"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Numar Inventar</Label>
                  <Input
                    value={formData.inventory_number}
                    onChange={(e) => setFormData({ ...formData, inventory_number: e.target.value })}
                    className="h-11"
                    placeholder="ex: INV-00123"
                    data-testid="eq-inventory-number"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Utilizare</Label>
                  <Input
                    value={formData.utilizare}
                    onChange={(e) => setFormData({ ...formData, utilizare: e.target.value })}
                    className="h-11"
                    placeholder="ex: CENTRU PRELUCRARE PVC"
                    data-testid="eq-utilizare"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>An Fabricatie</Label>
                    <Input
                      type="number"
                      value={formData.an_fabricatie}
                      onChange={(e) => setFormData({ ...formData, an_fabricatie: e.target.value ? parseInt(e.target.value) : '' })}
                      className="h-11"
                      placeholder="2024"
                      min="1950"
                      max="2030"
                      data-testid="eq-an-fabricatie"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Putere Absorbita</Label>
                    <Input
                      value={formData.putere_absorbita}
                      onChange={(e) => setFormData({ ...formData, putere_absorbita: e.target.value })}
                      className="h-11"
                      placeholder="ex: 22 KW"
                      data-testid="eq-putere"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Descriere</Label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={2}
                  />
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setShowAddDialog(false)}>Anuleaza</Button>
                  <Button type="submit" className="btn-primary" data-testid="save-equipment-btn">
                    {editItem ? 'ACTUALIZEAZA' : 'ADAUGA'}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {selectedIds.length > 0 && (
        <div className="flex items-center gap-3 bg-blue-50 border border-blue-200 rounded-lg px-4 py-3" data-testid="bulk-actions-equipment">
          <span className="text-sm font-medium text-blue-800">{selectedIds.length} selectate</span>
          <div className="flex gap-2 ml-auto flex-wrap">
            <Button size="sm" variant="outline" className="text-blue-700 border-blue-300 hover:bg-blue-50" onClick={openBulkUpdateDialog} data-testid="bulk-update-fields-btn">
              <RefreshCw className="w-4 h-4 mr-1" /> Actualizare in masa
            </Button>
            <Button size="sm" variant="outline" className="text-green-700 border-green-300 hover:bg-green-50" onClick={() => handleBulkStatus(true)} data-testid="bulk-activate-eq">
              <CheckCircle2 className="w-4 h-4 mr-1" /> Activeaza
            </Button>
            <Button size="sm" variant="outline" className="text-red-700 border-red-300 hover:bg-red-50" onClick={() => handleBulkStatus(false)} data-testid="bulk-deactivate-eq">
              <XCircle className="w-4 h-4 mr-1" /> Dezactiveaza
            </Button>
            <Button size="sm" variant="outline" className="text-red-700 border-red-300 hover:bg-red-50" onClick={handleBulkDelete} data-testid="bulk-delete-eq">
              <Trash2 className="w-4 h-4 mr-1" /> Sterge
            </Button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0052CC]"></div>
        </div>
      ) : (isMobile || activeView === 'cards') ? (
        <div className={isMobile ? "space-y-2" : "grid md:grid-cols-2 lg:grid-cols-3 gap-4"}>
          {equipmentData.length > 0 ? equipmentData.map((eq) => isMobile ? (
            <div
              key={eq.id}
              className="bg-white rounded-xl border border-slate-200 p-3.5 active:bg-slate-50 transition-colors"
              onClick={() => handleEdit(eq)}
              data-testid={`mobile-eq-${eq.code}`}
            >
              <div className="flex items-start justify-between gap-2 mb-1.5">
                <div className="flex items-center gap-2.5 flex-1 min-w-0">
                  <div className="w-9 h-9 rounded-lg bg-blue-100 flex items-center justify-center shrink-0">
                    <Cog className="w-4 h-4 text-[#0052CC]" />
                  </div>
                  <div className="min-w-0">
                    <h3 className="text-sm font-semibold text-slate-900 line-clamp-1">{eq.name}</h3>
                    <p className="text-xs text-slate-500 font-mono">{eq.code}</p>
                  </div>
                </div>
                <ChevronRight className="w-4 h-4 text-slate-300 shrink-0 mt-0.5" />
              </div>
              <div className="flex items-center gap-1.5 flex-wrap mt-2">
                {eq.category_name && <Badge variant="secondary" className="text-[10px] px-1.5 py-0">{eq.category_name}</Badge>}
                {eq.is_active === false
                  ? <Badge className="bg-red-100 text-red-600 text-[10px] px-1.5 py-0">Inactiv</Badge>
                  : <Badge className="bg-green-100 text-green-600 text-[10px] px-1.5 py-0">Activ</Badge>}
              </div>
              <div className="flex items-center gap-3 mt-2.5 pt-2 border-t border-slate-100 text-[11px] text-slate-500">
                {eq.location_name && <span className="truncate">{eq.location_name}</span>}
                {eq.manufacturer && <span className="truncate">{eq.manufacturer}</span>}
                {eq.serial_number && <span className="font-mono ml-auto">{eq.serial_number}</span>}
              </div>
            </div>
          ) : (
            <Card key={eq.id} className="card-industrial hover:shadow-md transition-shadow" data-testid={`equipment-card-${eq.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3">
                    <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center">
                      <Cog className="w-6 h-6 text-[#0052CC]" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="font-semibold text-slate-900">{eq.name}</p>
                        <Badge variant="outline" className="font-mono text-xs">{eq.code}</Badge>
                      </div>
                      <div className="flex items-center gap-2 mt-1 text-sm text-slate-500">
                        {eq.category_name && <Badge variant="secondary">{eq.category_name}</Badge>}
                        <span>{eq.location_name}</span>
                      </div>
                      {eq.manufacturer && (
                        <p className="text-xs text-slate-400 mt-1">{eq.manufacturer} {eq.model}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="sm" onClick={() => showQrCode(eq)}>
                      <QrCode className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => handleEdit(eq)}>
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => handleDelete(eq.id)} className="text-red-600">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )) : (
            <Card className="card-industrial col-span-full">
              <CardContent className="flex flex-col items-center justify-center py-12">
                <Cog className="w-16 h-16 text-slate-300 mb-4" />
                <p className="text-slate-500">Nu exista echipamente</p>
              </CardContent>
            </Card>
          )}
        </div>
      ) : (
        <DataTable
          data={equipmentData}
          columns={tableColumns}
          pageSize={10}
          searchable={true}
          exportable={true}
          printable={true}
          selectable={true}
          onSelectionChange={(ids) => setSelectedIds(ids)}
          onRowClick={handleEdit}
          emptyMessage="Nu exista echipamente inregistrate"
        />
      )}

      {/* QR Code Dialog */}
      <Dialog open={!!qrDialog} onOpenChange={() => setQrDialog(null)}>
        <DialogContent className="max-w-sm text-center">
          <DialogHeader>
            <DialogTitle>Cod QR Echipament</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col items-center gap-4 py-4">
            {qrDialog?.qr_code && (
              <div className="border-2 border-slate-200 rounded-lg p-4 bg-white">
                <img 
                  src={`data:image/png;base64,${qrDialog.qr_code}`} 
                  alt="QR Code"
                  className="w-44 h-44"
                />
                <div className="mt-3 pt-3 border-t border-slate-200">
                  <p className="font-mono text-lg font-bold text-slate-900">{qrDialog?.code}</p>
                  <p className="text-sm text-slate-600 mt-1">{qrDialog?.name}</p>
                </div>
              </div>
            )}
            {qrDialog?.qr_url && (
              <p className="text-xs text-slate-400 break-all">{qrDialog.qr_url}</p>
            )}
            <p className="text-sm text-slate-500">
              Scaneaza pentru raportare rapida probleme
            </p>
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => {
                  const link = document.createElement('a');
                  link.href = `data:image/png;base64,${qrDialog.qr_code}`;
                  link.download = `QR_${qrDialog.code || qrDialog.name}.png`;
                  link.click();
                }}
                data-testid="qr-dialog-download-png"
              >
                <Download className="w-4 h-4 mr-1" /> PNG
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => downloadEquipmentPdf({ id: qrDialog.id, code: qrDialog.code, name: qrDialog.name })}
                data-testid="qr-dialog-download-pdf"
              >
                <Download className="w-4 h-4 mr-1" /> PDF
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => {
                  window.print();
                }}
              >
                <Printer className="w-4 h-4 mr-1" /> Print
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* QR Export Format Dialog */}
      <Dialog open={showQrExportDialog} onOpenChange={setShowQrExportDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Export Coduri QR</DialogTitle>
            <DialogDescription>Selectati campurile si formatul etichetelor QR</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-slate-500">
              Se vor exporta {selectedIds.length > 0 ? selectedIds.length : equipment.length} echipamente
            </p>
            
            {/* Field Selection */}
            <div className="space-y-3">
              <p className="text-sm font-medium text-slate-700">Campuri afisate pe eticheta:</p>
              <div className="grid grid-cols-2 gap-2">
                {qrFieldOptions.map(field => (
                  <div key={field.key} className="flex items-center space-x-2">
                    <Checkbox
                      id={`eq-field-${field.key}`}
                      checked={qrFields[field.key]}
                      onCheckedChange={(checked) => setQrFields({ ...qrFields, [field.key]: checked })}
                    />
                    <label htmlFor={`eq-field-${field.key}`} className="text-sm cursor-pointer">{field.label}</label>
                  </div>
                ))}
              </div>
            </div>
            
            <Separator />
            
            {/* Format Selection */}
            <div className="grid gap-3">
              <Button variant="outline" className="h-auto py-4 justify-start" onClick={() => handleExportQrCodes('standard')}>
                <div className="text-left">
                  <p className="font-semibold">Standard (24/pagina)</p>
                  <p className="text-xs text-slate-500">3 coloane x 8 randuri - ideal pentru A4</p>
                </div>
              </Button>
              <Button variant="outline" className="h-auto py-4 justify-start" onClick={() => handleExportQrCodes('large')}>
                <div className="text-left">
                  <p className="font-semibold">Mari (8/pagina)</p>
                  <p className="text-xs text-slate-500">2 coloane x 4 randuri - etichete mari vizibile</p>
                </div>
              </Button>
              <Button variant="outline" className="h-auto py-4 justify-start" data-testid="qr-format-industrial" onClick={() => handleExportQrCodes('industrial')}>
                <div className="text-left">
                  <p className="font-semibold">Industrial (16/pagina)</p>
                  <p className="text-xs text-slate-500">2 coloane x 8 randuri - 105x38mm etichete mici</p>
                </div>
              </Button>
              <Button variant="outline" className="h-auto py-4 justify-start" onClick={() => handleExportQrCodes('thermal')}>
                <div className="text-left">
                  <p className="font-semibold">Termic (50x30mm)</p>
                  <p className="text-xs text-slate-500">Pentru imprimante de etichete termice</p>
                </div>
              </Button>
              <Separator className="my-2" />
              <Button variant="outline" className="h-auto py-4 justify-start border-blue-200 hover:bg-blue-50" onClick={handleExportQrZip}>
                <div className="text-left">
                  <p className="font-semibold text-blue-700">Export JPEG (ZIP)</p>
                  <p className="text-xs text-slate-500">Imagini individuale cu campurile selectate</p>
                </div>
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bulk Update Fields Dialog */}
      <Dialog open={showBulkUpdateDialog} onOpenChange={setShowBulkUpdateDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Actualizare in masa</DialogTitle>
            <DialogDescription>
              Selectati campurile pe care doriti sa le actualizati pentru {selectedIds.length} echipamente
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            {[
              { key: 'category_id', label: 'Categorie', type: 'select' },
              { key: 'plant_id', label: 'Sediu', type: 'select' },
              { key: 'location_id', label: 'Locatie', type: 'select' },
              { key: 'ticket_priority', label: 'Prioritate Tichet', type: 'priority' },
              { key: 'parent_id', label: 'Echipament parinte', type: 'select' },
              { key: 'manufacturer', label: 'Producator', type: 'text' },
              { key: 'model', label: 'Model', type: 'text' },
              { key: 'serial_number', label: 'Serie', type: 'text' },
              { key: 'inventory_number', label: 'Numar Inventar', type: 'text' },
              { key: 'utilizare', label: 'Utilizare', type: 'text' },
              { key: 'an_fabricatie', label: 'An Fabricatie', type: 'number' },
              { key: 'putere_absorbita', label: 'Putere Absorbita', type: 'text' },
              { key: 'description', label: 'Descriere', type: 'text' },
            ].map(field => (
              <div key={field.key} className={`rounded-lg border p-3 transition-colors ${bulkUpdateFields[field.key] ? 'border-blue-300 bg-blue-50/50' : 'border-slate-200'}`}>
                <div className="flex items-center gap-2">
                  <Checkbox
                    id={`bulk-${field.key}`}
                    checked={!!bulkUpdateFields[field.key]}
                    onCheckedChange={(checked) => {
                      setBulkUpdateFields(prev => ({ ...prev, [field.key]: checked }));
                      if (!checked) setBulkUpdateValues(prev => { const n = { ...prev }; delete n[field.key]; return n; });
                    }}
                    data-testid={`bulk-check-${field.key}`}
                  />
                  <label htmlFor={`bulk-${field.key}`} className="text-sm font-medium cursor-pointer flex-1">{field.label}</label>
                </div>
                {bulkUpdateFields[field.key] && (
                  <div className="mt-2 pl-6">
                    {field.key === 'category_id' ? (
                      <Select value={bulkUpdateValues.category_id || ''} onValueChange={v => setBulkUpdateValues(prev => ({ ...prev, category_id: v }))}>
                        <SelectTrigger className="h-10" data-testid="bulk-val-category"><SelectValue placeholder="Selecteaza categorie" /></SelectTrigger>
                        <SelectContent>{categories.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                      </Select>
                    ) : field.key === 'plant_id' ? (
                      <Select value={bulkUpdateValues.plant_id || ''} onValueChange={v => setBulkUpdateValues(prev => ({ ...prev, plant_id: v, ...(prev.location_id ? { location_id: '' } : {}) }))}>
                        <SelectTrigger className="h-10" data-testid="bulk-val-plant"><SelectValue placeholder="Selecteaza sediu" /></SelectTrigger>
                        <SelectContent>{plants.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                      </Select>
                    ) : field.key === 'location_id' ? (
                      <Select value={bulkUpdateValues.location_id || ''} onValueChange={v => setBulkUpdateValues(prev => ({ ...prev, location_id: v }))}>
                        <SelectTrigger className="h-10" data-testid="bulk-val-location"><SelectValue placeholder="Selecteaza locatie" /></SelectTrigger>
                        <SelectContent>{locations.filter(l => !bulkUpdateValues.plant_id || l.plant_id === bulkUpdateValues.plant_id).map(l => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}</SelectContent>
                      </Select>
                    ) : field.key === 'parent_id' ? (
                      <Combobox
                        items={equipment.filter(e => !selectedIds.includes(e.id))}
                        value={bulkUpdateValues.parent_id || ''}
                        onSelect={v => setBulkUpdateValues(prev => ({ ...prev, parent_id: v || null }))}
                        placeholder="Niciun parinte"
                        searchPlaceholder="Cauta echipament..."
                        codeKey="code"
                      />
                    ) : field.type === 'priority' ? (
                      <Select value={bulkUpdateValues.ticket_priority || ''} onValueChange={v => setBulkUpdateValues(prev => ({ ...prev, ticket_priority: v }))}>
                        <SelectTrigger className="h-10" data-testid="bulk-val-priority"><SelectValue placeholder="Selecteaza prioritate" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="critical">Critic</SelectItem>
                          <SelectItem value="urgent">Urgent</SelectItem>
                          <SelectItem value="high">Ridicat</SelectItem>
                          <SelectItem value="medium">Mediu</SelectItem>
                          <SelectItem value="low">Scazut</SelectItem>
                        </SelectContent>
                      </Select>
                    ) : field.type === 'number' ? (
                      <Input type="number" value={bulkUpdateValues[field.key] || ''} onChange={e => setBulkUpdateValues(prev => ({ ...prev, [field.key]: e.target.value }))} className="h-10" placeholder={`Noua valoare ${field.label.toLowerCase()}`} min="1950" max="2030" data-testid={`bulk-val-${field.key}`} />
                    ) : (
                      <Input value={bulkUpdateValues[field.key] || ''} onChange={e => setBulkUpdateValues(prev => ({ ...prev, [field.key]: e.target.value }))} className="h-10" placeholder={`Noua valoare ${field.label.toLowerCase()}`} data-testid={`bulk-val-${field.key}`} />
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBulkUpdateDialog(false)}>Anuleaza</Button>
            <Button className="btn-primary" onClick={handleBulkUpdateFields} disabled={!Object.values(bulkUpdateFields).some(v => v)} data-testid="bulk-update-submit">
              <RefreshCw className="w-4 h-4 mr-2" />
              Actualizeaza {selectedIds.length} echipamente
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import Equipment Dialog */}
      <Dialog open={showImportDialog} onOpenChange={setShowImportDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="w-5 h-5" />
              Import Echipamente din Excel
            </DialogTitle>
            <DialogDescription>
              Incarcati un fisier Excel (.xlsx) cu echipamente. Coloanele acceptate: Cod, Nume, Utilizare, Serie, Numar Inventar, An Fabricatie, Putere Absorbita, Categorie, Fabrica, Locatie, Producator, Model, Prioritate Tichet, Activ
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="border-2 border-dashed border-slate-300 rounded-lg p-6 text-center hover:border-blue-500 transition-colors cursor-pointer"
              onClick={() => importFileRef.current?.click()}
            >
              <input
                ref={importFileRef}
                type="file"
                accept=".xlsx,.xls"
                onChange={(e) => { setImportFile(e.target.files[0]); setImportResult(null); }}
                className="hidden"
                data-testid="import-file-input"
              />
              <FileSpreadsheet className="w-10 h-10 mx-auto text-slate-400 mb-2" />
              <p className="text-sm text-slate-600">
                {importFile ? importFile.name : 'Click pentru a selecta fisier Excel'}
              </p>
              <p className="text-xs text-slate-400 mt-1">Formate acceptate: .xlsx, .xls</p>
            </div>

            <div className="bg-slate-50 rounded-lg p-3 text-xs text-slate-600 space-y-1">
              <p className="font-medium text-slate-700">Coloane acceptate:</p>
              <p>Cod, Nume, Utilizare, Serie, Numar Inventar, An Fabricatie, Putere Absorbita, Categorie, Fabrica, Locatie, Producator, Model, <span className="font-semibold text-blue-700">Prioritate Tichet</span>, Activ</p>
              <p className="mt-1">Valori Prioritate Tichet: <span className="font-mono">CRITIC, URGENT, RIDICAT, MEDIU, SCAZUT</span></p>
            </div>

            {importResult && (
              <div className={`rounded-lg p-3 text-sm ${importResult.success ? 'bg-green-50 border border-green-200 text-green-700' : 'bg-red-50 border border-red-200 text-red-700'}`}>
                <p className="font-medium">{importResult.message}</p>
                {importResult.errors?.length > 0 && (
                  <ul className="mt-1 list-disc list-inside text-xs">
                    {importResult.errors.map((err, i) => <li key={i}>{err}</li>)}
                  </ul>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowImportDialog(false)}>Anuleaza</Button>
            <Button onClick={handleImportExcel} disabled={!importFile || importing} className="btn-primary" data-testid="import-submit-btn">
              {importing ? 'Se importa...' : 'Importa Echipamente'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AuditLogDialog
        open={!!auditDialog}
        onOpenChange={(open) => !open && setAuditDialog(null)}
        entityType="equipment"
        entityId={auditDialog?.id}
        entityName={auditDialog ? `${auditDialog.code} - ${auditDialog.name}` : ''}
      />
    </div>
  );
};

export default EquipmentPage;
